import { 
  Sparkles, 
  Maximize2, 
  Wand2, 
  Eraser, 
  Palette, 
  Layers,
  Zap,
  Play,
  Megaphone
} from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { cn } from "@/lib/utils";

const features = [
  {
    icon: Sparkles,
    title: "Génération d'images",
    description: "Créez des visuels uniques à partir d'une simple description textuelle. Notre IA comprend vos intentions.",
    size: "large",
    gradient: "from-primary/20 to-violet/20",
    hasDemo: true,
  },
  {
    icon: Megaphone,
    title: "Ad Studio",
    description: "Créez des publicités pro pour tous les réseaux sociaux.",
    size: "small",
    gradient: "from-accent/20 to-highlight/20",
    isNew: true,
  },
  {
    icon: Palette,
    title: "+100 Styles",
    description: "Cartoon, réaliste, anime, 3D et bien plus.",
    size: "small",
    gradient: "from-highlight/20 to-accent/20",
  },
  {
    icon: Wand2,
    title: "Retouches intelligentes",
    description: "Améliorations automatiques pour des photos parfaites en un clic.",
    size: "medium",
    gradient: "from-violet/20 to-primary/20",
  },
  {
    icon: Eraser,
    title: "Suppression d'objets",
    description: "Effacez les éléments indésirables comme par magie.",
    size: "medium",
    gradient: "from-primary/20 to-accent/20",
    hasAnimation: true,
  },
  {
    icon: Maximize2,
    title: "Upscale 4K",
    description: "Augmentez la résolution sans perte de qualité.",
    size: "small",
    gradient: "from-accent/20 to-primary/20",
  },
  {
    icon: Layers,
    title: "Floop Studio",
    description: "Éditeur complet intégré pour un contrôle total sur vos créations. Combinez tous les outils en un seul workflow.",
    size: "wide",
    gradient: "from-accent/20 via-primary/20 to-violet/20",
  },
];

const FeatureCard = ({ feature, index }: { feature: typeof features[0]; index: number }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={cardRef}
      className={cn(
        "group relative rounded-3xl border border-border overflow-hidden transition-all duration-500 bg-card/80 backdrop-blur-xl",
        "hover:border-primary/50 hover:shadow-2xl hover:shadow-primary/10",
        feature.size === "large" && "md:col-span-2 md:row-span-2",
        feature.size === "medium" && "md:col-span-1 md:row-span-1",
        feature.size === "small" && "md:col-span-1 md:row-span-1",
        feature.size === "wide" && "md:col-span-2 md:row-span-1",
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
      )}
      style={{ 
        transitionDelay: `${index * 100}ms`,
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Gradient background */}
      <div className={cn(
        "absolute inset-0 bg-gradient-to-br opacity-0 transition-opacity duration-500",
        feature.gradient,
        isHovered && "opacity-100"
      )} />
      
      {/* Content */}
      <div className={cn(
        "relative z-10 p-6 h-full flex flex-col",
        feature.size === "large" && "p-8"
      )}>
        <div className={cn(
          "w-14 h-14 rounded-2xl flex items-center justify-center mb-4 transition-all duration-500",
          "bg-secondary border border-border",
          isHovered && "bg-gradient-floop border-transparent scale-110"
        )}>
          <feature.icon className={cn(
            "w-7 h-7 transition-colors duration-300",
            isHovered ? "text-white" : "text-primary"
          )} />
        </div>
        
        <h3 className={cn(
          "font-bold text-foreground mb-2 font-display",
          feature.size === "large" ? "text-2xl" : "text-lg"
        )}>
          {feature.title}
        </h3>
        
        <p className={cn(
          "text-muted-foreground leading-relaxed",
          feature.size === "large" ? "text-base" : "text-sm"
        )}>
          {feature.description}
        </p>

        {/* Demo area for large card */}
        {feature.hasDemo && (
          <div className="mt-6 flex-1 rounded-2xl bg-secondary/50 border border-border overflow-hidden relative min-h-[200px]">
            <div className="absolute inset-0 flex items-center justify-center">
              <div className={cn(
                "w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center transition-all duration-300",
                isHovered && "scale-110 bg-primary/30"
              )}>
                <Play className="w-6 h-6 text-primary fill-primary" />
              </div>
            </div>
            <div className="absolute bottom-4 left-4 right-4">
              <div className="bg-card/90 backdrop-blur-sm rounded-lg p-3 border border-border">
                <div className="flex items-center gap-2">
                  <Zap className="w-4 h-4 text-highlight" />
                  <span className="text-xs text-muted-foreground">Génération en ~3s</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Animation preview for eraser card */}
        {feature.hasAnimation && (
          <div className="mt-4 relative h-20 rounded-xl bg-secondary/50 overflow-hidden">
            <div className={cn(
              "absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-destructive/50 transition-all duration-700",
              isHovered && "opacity-0 scale-0"
            )} />
            <div className={cn(
              "absolute inset-0 flex items-center justify-center text-xs text-muted-foreground transition-opacity duration-500",
              isHovered ? "opacity-100" : "opacity-0"
            )}>
              ✨ Disparu !
            </div>
          </div>
        )}

        {/* New badge */}
        {feature.isNew && (
          <span className="absolute top-4 right-4 bg-accent text-accent-foreground text-[10px] font-bold px-2 py-0.5 rounded-full">
            NOUVEAU
          </span>
        )}
      </div>

      {/* Shine effect */}
      <div className={cn(
        "absolute inset-0 opacity-0 transition-opacity duration-500 pointer-events-none",
        isHovered && "opacity-100"
      )}>
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-primary/5 to-transparent -translate-x-full animate-shine" />
      </div>
    </div>
  );
};

export const BentoFeatures = () => {
  return (
    <section className="py-24 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 mesh-gradient-bg opacity-50" />
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
      
      <div className="container mx-auto px-6 relative z-10">
        <div className="text-center mb-16">
          <span className="inline-flex items-center gap-2 glass-card border border-primary/30 rounded-full px-5 py-2.5 mb-6 text-sm font-medium text-foreground">
            <Sparkles className="w-4 h-4 text-primary" />
            Fonctionnalités
          </span>
          
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-6 font-display">
            Une suite complète pour vos{" "}
            <span className="gradient-text-animated">créations</span>
          </h2>
          
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Tous les outils IA dont vous avez besoin, dans une interface simple et intuitive.
          </p>
        </div>

        {/* Bento Grid */}
        <div className="grid md:grid-cols-3 gap-4 max-w-6xl mx-auto">
          {features.map((feature, index) => (
            <FeatureCard key={index} feature={feature} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};
